import React, { useEffect } from 'react'
import {
  type FieldErrors,
  type UseFormSetValue,
  useForm,
  type Control
} from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import type { PackagesFormStateType, MakeWatchObject } from '../types'
import { fields as FormFields } from './utils'
import { hasHotel, isHotelPlusCar } from './TripTypes'

type ChildrenFunctionArgs = {
  setValue: UseFormSetValue<PackagesFormStateType>
  errors: FieldErrors<PackagesFormStateType>
  // eslint-disable-next-line @typescript-eslint/ban-types
  register: Function
  control: Control<PackagesFormStateType>
  watchState: MakeWatchObject<PackagesFormStateType>
}

type FormContainerProps = Readonly<{
  children: (args: ChildrenFunctionArgs) => JSX.Element
  initialState: PackagesFormStateType
  onSubmit: (data: PackagesFormStateType) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchFormSchema: any
  fields: typeof FormFields
}>

export default function FormContainer({
  children,
  initialState,
  onSubmit,
  fields,
  searchFormSchema
}: FormContainerProps) {
  const {
    register,
    control,
    trigger,
    unregister,
    setValue,
    clearErrors,
    formState,
    handleSubmit,
    watch
  } = useForm<PackagesFormStateType>({
    defaultValues: initialState,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange'
  })

  const { errors } = formState

  useEffect(() => {
    fields.forEach(name => {
      register(name)
    })
    return () => {
      fields.forEach(name => {
        unregister(name)
      })
    }
  }, [fields, register, unregister])

  const {
    tripType: watchTripType,
    partialStay: watchPartialStay,
    startLocation: watchStartLocation,
    endLocation: watchEndLocation,
    startDate: watchStartDate,
    endDate: watchEndDate,
    travelers: watchTravelers,
    hotelStartDate: watchHotelStartDate,
    hotelEndDate: watchHotelEndDate,
    pickupCarSomewhere: watchPickupCarSomewhere,
    rcLocation: watchRcLocation,
    rcPickupDate: watchRcPickupDate,
    rcDropoffDate: watchRcDropoffDate,
    rcPickupTime: watchRcPickupTime,
    rcDropoffTime: watchRcDropoffTime
  } = watch()

  useEffect(() => {
    clearErrors('startLocation')
  }, [clearErrors, watchStartLocation])
  useEffect(() => {
    clearErrors('endLocation')
  }, [clearErrors, watchEndLocation])
  useEffect(() => {
    clearErrors('rcLocation')
  }, [clearErrors, watchRcLocation])
  useEffect(() => {
    clearErrors('startDate')
  }, [clearErrors, watchStartDate])
  useEffect(() => {
    clearErrors('endDate')
  }, [clearErrors, watchEndDate])
  useEffect(() => {
    clearErrors('hotelStartDate')
  }, [clearErrors, watchHotelStartDate, watchPartialStay])
  useEffect(() => {
    clearErrors('hotelEndDate')
  }, [clearErrors, watchHotelEndDate, watchPartialStay])
  useEffect(() => {
    clearErrors('rcPickupDate')
  }, [clearErrors, watchRcPickupDate])
  useEffect(() => {
    clearErrors('rcDropoffDate')
  }, [clearErrors, watchRcDropoffDate])
  useEffect(() => {
    clearErrors('isInfantOnInternationalTravel')
  }, [clearErrors, watchStartLocation, watchEndLocation, watchTravelers])
  useEffect(() => {
    clearErrors('travelers.children')
  }, [clearErrors, watchTravelers])
  useEffect(() => {
    if (watchPickupCarSomewhere) clearErrors('rcDropoffDate')
  }, [clearErrors, watchPickupCarSomewhere])

  useEffect(() => {
    if (!hasHotel(watchTripType) || isHotelPlusCar(watchTripType)) {
      setValue('partialStay', false)
    }
  }, [setValue, watchTripType])

  useEffect(() => {
    void trigger('travelers.children')
  }, [trigger, watchTravelers])

  useEffect(() => {
    void trigger('travelers')
  }, [trigger, watchTravelers])

  return (
    <form aria-label="packages-search-form" onSubmit={handleSubmit(onSubmit)}>
      {children({
        setValue,
        errors,
        register,
        control,
        watchState: {
          watchTripType,
          watchTravelers,
          watchHotelStartDate,
          watchHotelEndDate,
          watchStartDate,
          watchEndDate,
          watchPartialStay,
          watchStartLocation,
          watchEndLocation,
          watchPickupCarSomewhere,
          watchRcLocation,
          watchRcPickupDate,
          watchRcDropoffDate,
          watchRcPickupTime,
          watchRcDropoffTime
        }
      })}
    </form>
  )
}
